/**
 * Read the React Video Editor IndexedDB autosave blob (same schema as the SDK).
 * Used to implement explicit save and tracks-only PATCH without a public editor-state API.
 */

const DB_NAME = 'VideoEditorProDB';
const DB_VERSION = 4;
const AUTOSAVE_STORE = 'autosave';

export interface RveEditorAutosaveState {
  tracks?: unknown[];
  durationInFrames?: number;
  aspectRatio?: unknown;
  backgroundColor?: string;
  playbackRate?: number;
  selectedItemId?: string | null;
  selectedItemIds?: string[];
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
    req.onsuccess = () => resolve(req.result);
  });
}

/** Return editor state last persisted by RVE for ``projectId``, or null if none. */
export async function loadVideoEditorAutosave(
  projectId: string,
): Promise<RveEditorAutosaveState | null> {
  if (typeof indexedDB === 'undefined') {
    return null;
  }
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([AUTOSAVE_STORE], 'readonly');
    const store = tx.objectStore(AUTOSAVE_STORE);
    const req = store.get(projectId);
    req.onsuccess = () => {
      const row = req.result as { editorState?: RveEditorAutosaveState } | undefined;
      db.close();
      resolve(row?.editorState ?? null);
    };
    req.onerror = () => {
      db.close();
      reject(req.error ?? new Error('IndexedDB read failed'));
    };
  });
}
